/* eslint-disable @typescript-eslint/no-deprecated -- the rule flags every ParentNode.querySelector call, whatever the overload */

/** Marks the chat's scrolling message column. */
export const CHAT_COLUMN_MARKER = { 'data-chat-column': '' } as const;
/** Marks the body of an agent reply, the box an inline widget fills. */
export const CHAT_REPLY_MARKER = { 'data-chat-reply': '' } as const;
/** Marks the side panel beside the chat. */
export const SIDE_PANEL_MARKER = { 'data-side-panel': '' } as const;

/** The share of the session a split side panel takes: `SIDE_PANEL_SPLIT_WIDTH_CLASS_NAME`. */
const SPLIT_PANEL_SHARE = 0.6924;
/** The column's side padding plus a reply's right margin, for a chat with no reply yet. */
const REPLY_INSET = 48;
/** The bounds the API accepts. */
const MIN_WIDTH = 200;
const MAX_WIDTH = 8000;

export interface ClientLayout {
  inlineWidth: number;
  panelWidth: number;
}

const widthOf = (element: Element | null | undefined) =>
  element ? Math.round(element.getBoundingClientRect().width) : 0;

const clamp = (width: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width));

/**
 * The widths a widget gets right now: inside a reply, and in the side panel (as it is when open,
 * or as it would open beside the chat). Undefined when the chat is not laid out.
 */
export function measureChatLayout(root: ParentNode = document): ClientLayout | undefined {
  // A page can keep a second chat mounted but hidden, as the agent builder does.
  const column = Array.from(root.querySelectorAll('[data-chat-column]')).find(
    (candidate) => widthOf(candidate) >= MIN_WIDTH,
  );
  if (!column) return undefined;
  const columnWidth = widthOf(column);

  const replies = column.querySelectorAll('[data-chat-reply]');
  const replyWidth = widthOf(replies[replies.length - 1]);
  const inlineWidth = replyWidth || columnWidth - REPLY_INSET;

  const openPanelWidth = widthOf(root.querySelector('[data-side-panel]'));
  const panelWidth =
    openPanelWidth >= MIN_WIDTH ? openPanelWidth : Math.round(columnWidth * SPLIT_PANEL_SHARE);

  return { inlineWidth: clamp(inlineWidth), panelWidth: clamp(panelWidth) };
}
